/**
 * KingZoo Currency & Language Switcher
 * - No automatic language redirect
 * - Top-right widget: currency (€/₺) + language (EN/TR)
 * - EUR prices rounded to nearest 0.5
 * - Mobile-friendly
 */
(function () {
  'use strict';

  var CACHE_KEY     = 'kz_eur_rate';
  var CACHE_TTL     = 3600000; // 1 hour
  var CURRENCY_KEY  = 'kz_currency'; // 'EUR' | 'TRY'
  // Fixed rate — update manually when needed (1 TRY = ? EUR)
  var FIXED_RATE    = 0.0195; // 1 EUR = 51.28 TRY (updated 2026-04-06)

  var path = window.location.pathname;
  var isEN = path.indexOf('/en') === 0;

  // --- Current state ---
  // Currency follows language: TR→TRY, EN→EUR (override any saved preference)
  var currentCurrency = isEN ? 'EUR' : 'TRY';
  var currentLang    = isEN ? 'en' : 'tr';
  var rate           = null;

  // --- Rate cache ---
  function getCachedRate() {
    try {
      var item = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (item && Date.now() - item.ts < CACHE_TTL) return item.rate;
    } catch (e) {}
    return null;
  }
  function setCachedRate(r) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ rate: r, ts: Date.now() })); } catch (e) {}
  }

  // --- Parse TRY amount from text ---
  function parseTRY(text) {
    var clean = text.replace(/[₺\s]/g, '').replace(/TL/gi, '').trim();
    if (/\d\.\d{3},/.test(clean)) {
      clean = clean.replace(/\./g, '').replace(',', '.');
    } else if (/\d,\d{3}\./.test(clean)) {
      clean = clean.replace(/,/g, '');
    } else {
      clean = clean.replace(',', '.');
    }
    var n = parseFloat(clean);
    return isNaN(n) ? null : n;
  }

  // --- Round to nearest 0.5 (44.1→44, 44.3→44.5, 44.8→45) ---
  function roundHalf(x) {
    return Math.round(x * 2) / 2;
  }

  // --- Format EUR (€44 or €44,50 — de-DE decimal) ---
  function formatEUR(amount) {
    var r = roundHalf(amount);
    if (r % 1 === 0) {
      return '\u20ac' + r.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }
    return '\u20ac' + r.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // --- Price selectors (Sleek/FoxEcom theme) ---
  var SELECTORS = [
    '.f-price-item--regular',
    '.f-price-item--sale',
    '.f-price-item',
    'span.money',
    '[data-product-price]',
  ].join(',');

  // --- Convert / restore a single price element ---
  function convertEl(el) {
    if (!el.dataset.kzOriginal) el.dataset.kzOriginal = el.textContent.trim();
    if (currentCurrency === 'EUR' && rate) {
      if (el.dataset.kzConverted === '1') return;
      var tryAmt = parseTRY(el.dataset.kzOriginal);
      if (!tryAmt) return;
      el.dataset.kzConverted = '1';
      el.textContent = formatEUR(tryAmt * rate);
    } else {
      el.textContent = el.dataset.kzOriginal || el.textContent;
      el.dataset.kzConverted = '0';
    }
  }

  function convertAll() {
    document.querySelectorAll(SELECTORS).forEach(convertEl);
    if (isEN && currentCurrency === 'EUR') addCartNote();
  }

  // --- Cart disclaimer (EN cart only) ---
  function addCartNote() {
    if (!window.location.pathname.match(/\/en\/cart/)) return;
    if (document.getElementById('kz-cart-note')) return;
    var form = document.querySelector('form[action*="/cart"]');
    if (!form) return;
    var p = document.createElement('p');
    p.id = 'kz-cart-note';
    p.style.cssText = 'font-size:12px;color:#888;margin-top:8px;text-align:center;';
    p.textContent = 'Prices shown in EUR for reference. Payment will be processed in TRY.';
    form.insertAdjacentElement('beforeend', p);
  }

  // --- MutationObserver — catch dynamically added prices ---
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(SELECTORS)) convertEl(node);
        if (node.querySelectorAll) node.querySelectorAll(SELECTORS).forEach(convertEl);
      });
    });
  });

  // --- Language navigation ---
  function switchLanguage(lang) {
    if (lang === currentLang) return;
    var p = window.location.pathname;
    var qs = window.location.search;
    // Set currency default based on language
    localStorage.setItem(CURRENCY_KEY, lang === 'en' ? 'EUR' : 'TRY');
    if (lang === 'en') {
      window.location.href = '/en' + (p === '/' ? '' : p) + qs;
    } else {
      window.location.href = (p.indexOf('/en') === 0 ? (p.slice(3) || '/') : p) + qs;
    }
  }

  // --- Currency switch ---
  function setCurrency(c) {
    currentCurrency = c;
    localStorage.setItem(CURRENCY_KEY, c);
    // Force re-evaluate all elements
    document.querySelectorAll(SELECTORS).forEach(function (el) {
      el.dataset.kzConverted = '0';
      convertEl(el);
    });
    // Update widget labels
    var lbl = document.querySelector('#kz-sw-curr .kz-label');
    if (lbl) lbl.textContent = c === 'EUR' ? '\u20ac' : '\u20ba';
    document.querySelectorAll('#kz-sw-curr .kz-opt').forEach(function (o) {
      o.classList.toggle('kz-on', o.dataset.v === c);
    });
  }

  // --- Build switcher widget ---
  function buildWidget() {
    var s = document.createElement('style');
    s.textContent =
      /* Normal flex child — inherits container alignment, no pixel guessing */
      '#kz-sw{display:inline-flex;align-items:center;gap:6px;' +
        'font-size:13px;font-family:inherit;margin-right:4px;}' +

      '.kz-sw-group{position:relative;}' +

      '.kz-sw-btn{display:inline-flex;align-items:center;gap:2px;' +
        'padding:4px 2px;background:transparent;color:inherit;' +
        'border:none;cursor:pointer;font-size:13px;line-height:1;' +
        'white-space:nowrap;opacity:.85;transition:opacity .15s;}' +
      '.kz-sw-btn:hover{opacity:1;}' +

      '.kz-arr{font-size:8px;opacity:.55;margin-left:1px;}' +

      '.kz-drop{display:none;position:absolute;top:calc(100% + 8px);right:0;' +
        'background:#fff;color:#111;border-radius:8px;overflow:hidden;min-width:54px;' +
        'box-shadow:0 6px 20px rgba(0,0,0,.14);border:1px solid rgba(0,0,0,.08);z-index:10001;}' +
      '.kz-sw-group.open .kz-drop{display:block;}' +

      '.kz-opt{padding:9px 14px;cursor:pointer;font-size:13px;color:#333;}' +
      '.kz-opt:hover{background:#f5f5f5;}' +
      '.kz-opt.kz-on{font-weight:700;color:#111;}' +

      '@media(max-width:1024px){' +
        '#kz-sw{gap:3px;font-size:11px;margin-right:2px;}' +
        '.kz-sw-btn{padding:3px 2px;}' +
        '.kz-opt{padding:10px 12px;font-size:11px;}' +
        '#kz-wh-link,#kz-wh-tabs{display:none !important;}' +
      '}';
    document.head.appendChild(s);

    var wrap = document.createElement('div');
    wrap.id = 'kz-sw';

    // Currency group
    var cg = document.createElement('div');
    cg.className = 'kz-sw-group';
    cg.id = 'kz-sw-curr';
    cg.innerHTML =
      '<button class="kz-sw-btn" aria-label="Currency">' +
        '<span class="kz-label">' + (currentCurrency === 'EUR' ? '\u20ac' : '\u20ba') + '</span>' +
        '<span class="kz-arr">\u25be</span>' +
      '</button>' +
      '<div class="kz-drop">' +
        '<div class="kz-opt' + (currentCurrency === 'EUR' ? ' kz-on' : '') + '" data-v="EUR">\u20ac</div>' +
        '<div class="kz-opt' + (currentCurrency === 'TRY' ? ' kz-on' : '') + '" data-v="TRY">\u20ba</div>' +
      '</div>';

    // Language group
    var lg = document.createElement('div');
    lg.className = 'kz-sw-group';
    lg.id = 'kz-sw-lang';
    lg.innerHTML =
      '<button class="kz-sw-btn" aria-label="Language">' +
        '<span class="kz-label">' + currentLang.toUpperCase() + '</span>' +
        '<span class="kz-arr">\u25be</span>' +
      '</button>' +
      '<div class="kz-drop">' +
        '<div class="kz-opt' + (currentLang === 'en' ? ' kz-on' : '') + '" data-v="en">EN</div>' +
        '<div class="kz-opt' + (currentLang === 'tr' ? ' kz-on' : '') + '" data-v="tr">TR</div>' +
      '</div>';

    wrap.appendChild(cg);
    wrap.appendChild(lg);

    // --- Wholesale + logo styles ---
    var whStyle = document.createElement('style');
    whStyle.textContent =
      /* Tabs directly inside #kz-sw, left of € */
      /* Desktop tabs inside #kz-sw */
      '#kz-wh-tabs{display:inline-flex;align-items:center;gap:0;margin-right:10px;' +
        'border-right:1px solid rgba(0,0,0,.12);padding-right:10px;}' +
      '.kz-wh-tab{padding:4px 12px;font-size:12px;font-family:inherit;text-decoration:none;color:inherit;' +
        'border:none;background:transparent;cursor:pointer;opacity:.65;transition:opacity .15s,color .15s;' +
        'white-space:nowrap;letter-spacing:.03em;font-weight:500;}' +
      '.kz-wh-tab:hover{opacity:1;}' +
      '.kz-wh-tab.active{opacity:1;color:#000;font-weight:500;}' +
      '.kz-wh-tab+.kz-wh-tab{border-left:1px solid rgba(0,0,0,.12);}' +
      '#kz-wh-badge{display:inline-flex;align-items:center;justify-content:center;' +
        'background:#c00;color:#fff;border-radius:50%;width:15px;height:15px;font-size:9px;' +
        'margin-left:3px;vertical-align:middle;}' +
      /* Mobile tab bar — full width below header */
      '#kz-wh-mobile{display:none;position:sticky;top:0;z-index:9999;' +
        'background:#fff;border-bottom:1px solid #eee;}' +
      '#kz-wh-mobile .kz-wh-tab{flex:1;text-align:center;padding:12px 4px;font-size:12px;' +
        'border-left:none;border-right:1px solid #eee;}' +
      '#kz-wh-mobile .kz-wh-tab:last-child{border-right:none;}' +
      /* Wholesale link for guest/normal */
      '#kz-wh-link{font-size:12px;font-family:inherit;padding:4px 2px;opacity:.7;' +
        'text-decoration:none;color:inherit;margin-right:10px;white-space:nowrap;' +
        'border-right:1px solid rgba(0,0,0,.12);padding-right:10px;}' +
      '#kz-wh-link:hover{opacity:1;}' +
      /* Logo → left */
      'header .header__logo{justify-content:flex-start !important;}' +
      '@media(max-width:1024px){' +
        '#kz-wh-tabs,#kz-wh-link{display:none !important;}' +
        '#kz-wh-mobile{display:flex;}' +
      '}';
    document.head.appendChild(whStyle);

    // --- Wholesale injection (reads from window.kzCustomer, no API call) ---
    (function() {
      var CART_KEY = 'kz_wholesale_cart';
      function getWholesaleQty() {
        try { return (JSON.parse(localStorage.getItem(CART_KEY)) || []).reduce(function(s,i){return s+i.qty;},0); } catch(e){return 0;}
      }

      var kzTags = (window.kzCustomer && Array.isArray(window.kzCustomer.tags))
        ? window.kzCustomer.tags.join(',').toUpperCase()
        : '';
      var isWholesale = kzTags.indexOf('B2BTR') !== -1 || kzTags.indexOf('B2BEXP') !== -1;

      if (isWholesale) {
        var qty = getWholesaleQty();
        var curPage = window.location.pathname;
        var lp = isEN ? '/en' : '';
        function isActive(slug){ return curPage.indexOf(slug) !== -1 ? ' active' : ''; }

        // Tabs go directly inside #kz-sw, prepended before currency group
        var tOrder = isEN ? 'Order Form' : 'Sipariş Formu';
        var tQuote = isEN ? 'My Quote' : 'Teklifim';

        var tabs = document.createElement('div');
        tabs.id = 'kz-wh-tabs';
        tabs.innerHTML =
          '<a class="kz-wh-tab' + isActive('wholesale-form') + '" href="' + lp + '/pages/wholesale-form">' + tOrder + '</a>' +
          '<a class="kz-wh-tab' + isActive('wholesale-list') + '" href="' + lp + '/pages/wholesale-list">' +
            tQuote + (qty > 0 ? '<span id="kz-wh-badge">' + qty + '</span>' : '') +
          '</a>';
        wrap.insertBefore(tabs, wrap.firstChild);

        // Also hide existing nav links (replaced by wholesale tabs)
        var nav = document.querySelector('.header__navigation');
        if (nav) nav.style.visibility = 'hidden';

        // Mobile: full-width tab bar below header
        var mobileBar = document.createElement('div');
        mobileBar.id = 'kz-wh-mobile';
        mobileBar.innerHTML =
          '<a class="kz-wh-tab' + isActive('wholesale-form') + '" href="' + lp + '/pages/wholesale-form">' + tOrder + '</a>' +
          '<a class="kz-wh-tab' + isActive('wholesale-list') + '" href="' + lp + '/pages/wholesale-list">' +
            tQuote + (qty > 0 ? '<span id="kz-wh-badge-m" style="display:inline-flex;align-items:center;justify-content:center;background:#c00;color:#fff;border-radius:50%;width:15px;height:15px;font-size:9px;margin-left:3px;vertical-align:middle;">' + qty + '</span>' : '') +
          '</a>';
        var hdr = document.querySelector('header[data-section-id]') || document.querySelector('header');
        if (hdr && hdr.parentNode) hdr.parentNode.insertBefore(mobileBar, hdr.nextSibling);

        // Cart icon → wholesale-list
        document.addEventListener('click', function(e) {
          var cartBtn = e.target.closest('.cart-drawer-button') || e.target.closest('a[href*="/cart"]');
          if (cartBtn) { e.preventDefault(); window.location.href = lp + '/pages/wholesale-list'; }
        }, true);

        // Product page: intercept Add to Cart & Buy it Now for wholesale
        if (window.location.pathname.indexOf('/products/') !== -1) {
          var csvData = window.kzCustomer && window.kzCustomer.csvData;
          if (csvData) {
            var CSV_CACHE_KEY = 'kz_wh_csv';
            var CART_KEY_WH = 'kz_wholesale_cart';

            try { localStorage.setItem(CSV_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: csvData })); } catch(e) {}

            function addProductToWholesaleCart(handle, qty, products) {
              var p = null;
              for (var i = 0; i < products.length; i++) {
                if (products[i].handle === handle) { p = products[i]; break; }
              }
              if (!p) return;
              var cart = [];
              try { cart = JSON.parse(localStorage.getItem(CART_KEY_WH)) || []; } catch(e) {}
              var idx = -1;
              for (var j = 0; j < cart.length; j++) { if (cart[j].product_id === p.product_id) { idx = j; break; } }
              if (idx > -1) { cart[idx].qty += qty; }
              else { cart.push(Object.assign({}, p, { qty: qty })); }
              try { localStorage.setItem(CART_KEY_WH, JSON.stringify(cart)); } catch(e) {}
              // Update badges (desktop + mobile)
              var total = cart.reduce(function(s, i) { return s + i.qty; }, 0);
              ['kz-wh-badge', 'kz-wh-badge-m'].forEach(function(id) {
                var b = document.getElementById(id);
                if (b) { b.textContent = total; b.style.display = 'inline-flex'; }
              });
            }

            function syncAddToCart(qty) {
              var pathParts = window.location.pathname.split('/products/');
              if (pathParts.length < 2) return;
              var handle = pathParts[1].split('?')[0].split('/')[0];
              addProductToWholesaleCart(handle, qty, csvData);
            }

            function showQuoteToast(qty) {
              var old = document.getElementById('kz-quote-toast');
              if (old) old.remove();
              var toast = document.createElement('div');
              toast.id = 'kz-quote-toast';
              toast.innerHTML =
                '<span>' + (isEN
                  ? '&#10003; Added to Quote (' + qty + ')'
                  : '&#10003; Teklifime eklendi (' + qty + ')') + '</span>' +
                '<a href="' + lp + '/pages/wholesale-list" style="color:#fff;margin-left:12px;text-decoration:underline;font-weight:600;">' +
                  (isEN ? 'View Quote →' : 'Teklifimi Gör →') + '</a>';
              toast.style.cssText =
                'position:fixed;top:16px;right:16px;background:#111;color:#fff;padding:14px 20px;' +
                'border-radius:8px;font-size:13px;z-index:99999;display:flex;align-items:center;' +
                'box-shadow:0 4px 20px rgba(0,0,0,.25);animation:kzToastIn .3s ease;';
              document.body.appendChild(toast);
              setTimeout(function() {
                toast.style.transition = 'opacity .3s';
                toast.style.opacity = '0';
                setTimeout(function() { toast.remove(); }, 300);
              }, 3000);
            }

            // Inject toast animation
            var toastStyle = document.createElement('style');
            toastStyle.textContent = '@keyframes kzToastIn{from{transform:translateY(-10px);opacity:0}to{transform:translateY(0);opacity:1}}';
            document.head.appendChild(toastStyle);

            // ── Cart-add interception (document-level, no global monkey-patching) ──
            // Catches: form submits, button clicks, AJAX cart-add from theme/apps
            // Strategy: hijack the user interaction BEFORE it reaches the network layer
            function isCartAddTarget(el) {
              if (!el) return false;
              // Form action contains /cart/add
              var form = el.closest && el.closest('form[action*="/cart/add"]');
              if (form) return true;
              // Button/element with cart-add data attributes (Sleek/FoxEcom theme)
              var btn = el.closest && (
                el.closest('[data-add-to-cart]') ||
                el.closest('.product-form__cart-submit') ||
                el.closest('button[name="add"]') ||
                el.closest('[data-product-form-submit]')
              );
              return !!btn;
            }

            // Document-level submit capture — catches all cart-add forms (even dynamically added)
            document.addEventListener('submit', function(e) {
              var form = e.target;
              if (form && form.matches && form.matches('form[action*="/cart/add"]')) {
                e.preventDefault();
                e.stopImmediatePropagation();
                var qtyEl = form.querySelector('[name="quantity"]');
                var qty = parseInt(qtyEl && qtyEl.value) || 1;
                syncAddToCart(qty);
                showQuoteToast(qty);
              }
            }, true);

            // Document-level click capture — catches button clicks before form submit fires
            document.addEventListener('click', function(e) {
              if (isCartAddTarget(e.target)) {
                e.preventDefault();
                e.stopImmediatePropagation();
                var form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
                var qtyEl = form && form.querySelector('[name="quantity"]');
                var qty = parseInt(qtyEl && qtyEl.value) || 1;
                syncAddToCart(qty);
                showQuoteToast(qty);
              }
            }, true);

            // Add to Cart button text → "Add to Quote" (interception handled above at document level)
            var atcForm = document.querySelector('form[action*="/cart/add"]');
            if (atcForm) {
              var atcBtn = atcForm.querySelector('[type="submit"]') || atcForm.querySelector('button');
              if (atcBtn) atcBtn.textContent = isEN ? 'Add to Quote' : 'Teklife Ekle';
            }

            // Buy it Now → hide completely for wholesale
            var buyNowWrap = document.querySelector('.shopify-payment-button');
            if (buyNowWrap) buyNowWrap.style.display = 'none';
            var buyNowBtn = document.querySelector('.shopify-payment-button button')
                         || document.querySelector('[name="buy_it_now"]')
                         || document.querySelector('button[data-buy-now]');
            if (buyNowBtn) {
              buyNowBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                var qtyEl = atcForm ? atcForm.querySelector('[name="quantity"]') : null;
                var qty = parseInt(qtyEl && qtyEl.value) || 1;
                syncAddToCart(qty);
                window.location.href = lp + '/pages/wholesale-list';
                return false;
              }, true);
            }
          }
        }

      } else {
        // Desktop: small "Wholesale" link before €
        var ws = document.createElement('a');
        ws.id = 'kz-wh-link';
        ws.href = '/pages/wholesale-signup';
        ws.textContent = 'Wholesale';
        wrap.insertBefore(ws, wrap.firstChild);

        // Mobile: inject "Wholesale" into bottom of hamburger menu drawer
        function injectMobileWholesaleLink() {
          if (document.getElementById('kz-mobile-wh')) return;
          // Insert right before the language selector row (above "English")
          var anchor = document.querySelector('.menu-drawer__localization-form') ||
                       document.querySelector('.drawer__footer .grid') ||
                       document.querySelector('.menu-drawer__footer');
          if (!anchor) return;
          var link = document.createElement('a');
          link.id = 'kz-mobile-wh';
          link.href = '/pages/wholesale-signup';
          link.textContent = 'Wholesale';
          link.style.cssText =
            'display:block;padding:14px 24px;font-size:15px;color:inherit;text-decoration:none;' +
            'border-bottom:1px solid #eee;margin-bottom:4px;opacity:.9;letter-spacing:.04em;' +
            'text-align:center;font-weight:500;';
          link.addEventListener('mouseenter', function(){ this.style.opacity='1'; });
          link.addEventListener('mouseleave', function(){ this.style.opacity='.75'; });
          anchor.parentNode.insertBefore(link, anchor);
        }

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', injectMobileWholesaleLink);
        } else {
          injectMobileWholesaleLink();
        }
      }
    })();

    // Hide old "English" localization selector
    var oldLoc =
      document.querySelector('.f-localization-form') ||
      document.querySelector('[data-localization-form]') ||
      document.querySelector('.localization-form') ||
      document.querySelector('header .disclosure') ||
      null;
    if (oldLoc) oldLoc.style.display = 'none';

    // Inject into header icon bar (Sleek/FoxEcom)
    var iconBar =
      document.querySelector('.header__icons--right .header__buttons') ||
      document.querySelector('.header__icons--right') ||
      null;

    if (iconBar) {
      iconBar.insertBefore(wrap, iconBar.firstChild);
    } else {
      wrap.style.cssText = 'position:fixed;top:44px;right:180px;z-index:10000;' +
        'display:inline-flex;align-items:center;gap:6px;font-size:13px;';
      document.body.appendChild(wrap);
    }

    // Events
    wrap.addEventListener('click', function (e) {
      var btn = e.target.closest('.kz-sw-btn');
      var opt = e.target.closest('.kz-opt');

      if (btn) {
        var grp = btn.closest('.kz-sw-group');
        var wasOpen = grp.classList.contains('open');
        wrap.querySelectorAll('.kz-sw-group.open').forEach(function (g) { g.classList.remove('open'); });
        if (!wasOpen) grp.classList.add('open');
        e.stopPropagation();
        return;
      }

      if (opt) {
        var grp = opt.closest('.kz-sw-group');
        var v = opt.dataset.v;
        if (grp.id === 'kz-sw-curr') setCurrency(v);
        else switchLanguage(v);
        grp.classList.remove('open');
        e.stopPropagation();
      }
    });

    document.addEventListener('click', function () {
      wrap.querySelectorAll('.kz-sw-group.open').forEach(function (g) { g.classList.remove('open'); });
    });
  }

  // --- Init rate + conversion ---
  function init(r) {
    rate = r;
    convertAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Build widget (defer-safe: DOM is ready when script runs)
  buildWidget();

  // Use fixed rate directly — no external API call
  init(getCachedRate() || FIXED_RATE);
})();